import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import profilePic from '../j.png'; // Import the image from src directory

const HeroContainer = styled.section`
  min-height: calc(100vh - var(--header-height));
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 5rem;
  padding-bottom: 7rem;
  position: relative;
  z-index: 1;
  overflow: visible;
  
  /* Enhanced background with richer gradient */
  background-color: #0a0403;
  background-image: linear-gradient(135deg, #0a0403 0%, #221f1c 70%, #2a2522 100%);
  
  /* Add subtle texture overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4' viewBox='0 0 4 4'%3E%3Cpath fill='%23b45128' fill-opacity='0.05' d='M1 3h1v1H1V3zm2-2h1v1H3V1z'%3E%3C/path%3E%3C/svg%3E");
    opacity: 0.5;
    z-index: -1;
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 4rem;
  max-width: 1300px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroText = styled.div`
  max-width: 650px;
  
  h1 {
    font-size: 3.2rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
`;

const Greeting = styled.span`
  font-weight: 400;
  /* Improved warm color with better readability */
  color: #e8a383;
  display: block;
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const NameHighlight = styled.span`
  font-weight: 700;
  /* Enhanced gradient with smoother transition and better visibility */
  background: linear-gradient(90deg, #ffd166, #fff9c4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 4px;
    bottom: -8px;
    left: 0;
    /* More vibrant underline gradient */
    background: linear-gradient(90deg, #cf5c2c, #a33411);
    border-radius: 2px;
    box-shadow: 0 0 15px rgba(207, 92, 44, 0.7);
  }
`;

const ProfessionIntro = styled.div`
  font-size: 1.8rem;
  font-weight: 500;
  margin-bottom: 2rem;
  /* Warmer, more saturated color for better readability */
  color: #f0b396;
  min-height: 2.2rem; /* Ensures consistent height during animation */
  display: flex;
  
  .typing-text {
    position: relative;
    margin-left: 8px;
    /* Enhanced gold for better emphasis */
    color: #ffd166;
  }
  
  .typing-text::after {
    content: '|';
    position: absolute;
    right: -8px;
    /* Brighter cursor color for better visibility */
    color: #f27d52;
    animation: blink 0.7s infinite;
  }
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const BioText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  /* Lighter, more saturated color for better readability */
  color: #e9b8a1;
  max-width: 600px;
  margin-bottom: 1rem;
  padding-left: 1rem;
  /* Brighter accent border */
  border-left: 3px solid #e05e2c;
  
  @media (max-width: 768px) {
    padding-left: 0;
    border-left: none;
    text-align: center;
  }
`;

const HeroSocial = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2.5rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  /* Brighter initial state for better visibility */
  color: #e9b8a1;
  font-size: 1.6rem;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 50%;
    /* Enhanced accent color */
    background: #f27d52;
    transition: all 0.3s ease;
    transform: translateX(-50%);
    opacity: 0;
  }
  
  &:hover {
    /* More vibrant hover state */
    color: #ffd166;
    transform: translateY(-4px) scale(1.1);
  }
  
  &:hover::after {
    width: 100%;
    opacity: 1;
  }
`;

const HeroImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const ProfileImage = styled.img`
  width: clamp(250px, 80%, 420px);
  border-radius: 50%;
  object-fit: cover;
  aspect-ratio: 1 / 1;
  /* Enhanced border color */
  border: 4px solid #e05e2c;
  /* Improved glow effect with layered shadows */
  box-shadow: 0 0 15px rgba(224, 94, 44, 0.6),
              0 0 40px rgba(90, 30, 10, 0.5),
              0 10px 60px rgba(224, 94, 44, 0.3);
  filter: contrast(1.05) saturate(1.05);
  transition: transform var(--transition-speed-slow) var(--transition-effect),
              box-shadow var(--transition-speed-slow) var(--transition-effect),
              filter var(--transition-speed-slow) var(--transition-effect);
  will-change: transform, filter;
  
  &:hover {
    transform: scale(1.06) rotate(2deg);
    /* More vibrant glow on hover */
    box-shadow: 0 0 25px #ff7d52,
                0 0 50px rgba(90, 30, 10, 0.5),
                0 15px 80px rgba(224, 94, 44, 0.6);
    filter: contrast(1.1) saturate(1.15);
  }
`;

const BioContainer = styled.div`
  margin-bottom: 2rem;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: linear-gradient(90deg, #e05e2c, #cf5c2c);
  color: #fff;
  font-weight: 600;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(224, 94, 44, 0.3);
  
  &:hover {
    background: linear-gradient(90deg, #f27d52, #e05e2c);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(224, 94, 44, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const HeroSection = () => {
  const [professionIndex, setProfessionIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const professions = ['Web Developer', 'Designer', 'Analyst'];
  
  useEffect(() => {
    const typingSpeed = isDeleting ? 50 : 150; // Faster when deleting
    const pauseTime = 1500; // Time to pause at full word
    
    const currentProfession = professions[professionIndex];
    
    // If we're done typing the full word
    if (!isDeleting && displayText === currentProfession) {
      // Pause before starting to delete
      const timeout = setTimeout(() => {
        setIsDeleting(true);
      }, pauseTime);
      return () => clearTimeout(timeout);
    }
    
    // If we've deleted the entire word
    if (isDeleting && displayText === '') {
      setIsDeleting(false);
      // Move to next profession
      setProfessionIndex((prevIndex) => (prevIndex + 1) % professions.length);
      return;
    }
    
    // Handle typing/deleting animation
    const timeout = setTimeout(() => {
      setDisplayText(prev => {
        if (isDeleting) {
          return prev.substring(0, prev.length - 1);
        } else {
          return currentProfession.substring(0, prev.length + 1);
        }
      });
    }, typingSpeed);
    
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, professionIndex, professions]);
  
  return (
    <HeroContainer id="home">
      <HeroContent>
        <HeroText>
          <h1>
            <Greeting>Hello, I'm</Greeting>
            <NameHighlight>Rakshit Waze</NameHighlight>
          </h1>
          <ProfessionIntro>
            <span>I am a</span> <span className="typing-text">{displayText}</span>
          </ProfessionIntro>
          
          <BioContainer>
            <BioText>
              I build exceptional digital experiences with a focus on performance, 
              accessibility, and cutting-edge design.
            </BioText>
            <BioText>
              Specializing in creating responsive web applications and interactive 
              user interfaces that deliver results.
            </BioText>
          </BioContainer>
          
          <CTAButton href="#contact" className="btn">Get In Touch</CTAButton>
          
          <HeroSocial>
            <SocialLink href="https://github.com" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-github"></i>
            </SocialLink>
            <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-linkedin"></i>
            </SocialLink>
            <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-twitter"></i>
            </SocialLink>
            <SocialLink href="mailto:contact@example.com">
              <i className="fas fa-envelope"></i>
            </SocialLink>
          </HeroSocial>
        </HeroText>
        
        <HeroImageContainer>
          <ProfileImage src={profilePic} alt="Rakshit Waze" />
        </HeroImageContainer>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;